import { useCallback, useEffect, useState } from 'react';
import { QuyDoiDonViTinhChiTiet, QuyDoiDonViTinhChiTietInput, QuyDoiDonViTinhChiTietResponse } from '@/types/schemas';
import { useAuth } from '@/contexts/auth-context';
import { QUERY_KEYS } from '@/constants';
import api from '@/lib/api';

interface UseQuyDoiDonViTinhChiTietReturn {
  quyDoiDonViTinhChiTiets: QuyDoiDonViTinhChiTiet[];
  isLoading: boolean;
  totalItems: number;
  currentPage: number;
  handlePageChange: (page: number) => Promise<void>;
  addQuyDoiDonViTinhChiTiet: (newData: QuyDoiDonViTinhChiTietInput) => Promise<QuyDoiDonViTinhChiTiet>;
  updateQuyDoiDonViTinhChiTiet: (
    uuid: string,
    updatedData: QuyDoiDonViTinhChiTietInput
  ) => Promise<QuyDoiDonViTinhChiTiet>;
  deleteQuyDoiDonViTinhChiTiet: (uuid: string) => Promise<void>;
  refreshQuyDoiDonViTinhChiTiets: () => Promise<void>;
  getQuyDoiDonViTinhChiTietsByMaVT: (maVT: string) => Promise<QuyDoiDonViTinhChiTiet[]>;
}

/**
 * Hook for managing QuyDoiDonViTinhChiTiet (Detailed Unit Conversion) data
 *
 * This hook provides functions to fetch, create, update, and delete detailed unit conversions.
 */
export const useQuyDoiDonViTinhChiTiet = (
  initialData: QuyDoiDonViTinhChiTiet[] = [],
  pageSize: number = 10
): UseQuyDoiDonViTinhChiTietReturn => {
  const [quyDoiDonViTinhChiTiets, setQuyDoiDonViTinhChiTiets] = useState<QuyDoiDonViTinhChiTiet[]>(initialData);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);

  const { entity } = useAuth();

  const fetchQuyDoiDonViTinhChiTiets = useCallback(
    async (page: number = 0, pageSizeParam: number = pageSize) => {
      if (!entity?.slug) return;

      setIsLoading(true);
      try {
        const response = await api.get<QuyDoiDonViTinhChiTietResponse>(
          `/entities/${entity.slug}/erp/${QUERY_KEYS.QUY_DOI_DON_VI_TINH_CHI_TIET}/`,
          {
            params: {
              page: page + 1, // API uses 1-based pagination
              page_size: pageSizeParam
            }
          }
        );
        setQuyDoiDonViTinhChiTiets(response.data.results);
        setTotalItems(response.data.count || response.data.results?.length || 0);
        setCurrentPage(page);
      } catch (error) {
        console.error('Error fetching detailed unit conversions:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [entity?.slug, pageSize]
  );

  const handlePageChange = useCallback(
    async (page: number) => {
      await fetchQuyDoiDonViTinhChiTiets(page, pageSize);
    },
    [fetchQuyDoiDonViTinhChiTiets, pageSize]
  );

  const refreshQuyDoiDonViTinhChiTiets = useCallback(async (): Promise<void> => {
    await fetchQuyDoiDonViTinhChiTiets(currentPage, pageSize);
  }, [fetchQuyDoiDonViTinhChiTiets, currentPage, pageSize]);

  const addQuyDoiDonViTinhChiTiet = async (newData: QuyDoiDonViTinhChiTietInput): Promise<QuyDoiDonViTinhChiTiet> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      // Ensure he_so is a number
      const processedData = {
        ...newData,
        he_so: typeof newData.he_so === 'string' ? parseFloat(newData.he_so) : newData.he_so
      };

      const response = await api.post<QuyDoiDonViTinhChiTiet>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.QUY_DOI_DON_VI_TINH_CHI_TIET}/`,
        processedData
      );
      const addedData = response.data;

      // Refresh current page to get updated data
      await refreshQuyDoiDonViTinhChiTiets();
      return addedData;
    } catch (error) {
      console.error('Error adding detailed unit conversion:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateQuyDoiDonViTinhChiTiet = async (
    uuid: string,
    updatedData: QuyDoiDonViTinhChiTietInput
  ): Promise<QuyDoiDonViTinhChiTiet> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      // Ensure he_so is a number
      const processedData = {
        ...updatedData,
        he_so: typeof updatedData.he_so === 'string' ? parseFloat(updatedData.he_so) : updatedData.he_so
      };

      const response = await api.patch<QuyDoiDonViTinhChiTiet>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.QUY_DOI_DON_VI_TINH_CHI_TIET}/${uuid}/`,
        processedData
      );
      const updatedItem = response.data;

      // Refresh current page to get updated data
      await refreshQuyDoiDonViTinhChiTiets();
      return updatedItem;
    } catch (error) {
      console.error('Error updating detailed unit conversion:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteQuyDoiDonViTinhChiTiet = async (uuid: string): Promise<void> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/${QUERY_KEYS.QUY_DOI_DON_VI_TINH_CHI_TIET}/${uuid}/`);
      // Refresh current page to get updated data
      await refreshQuyDoiDonViTinhChiTiets();
    } catch (error) {
      console.error('Error deleting detailed unit conversion:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const getQuyDoiDonViTinhChiTietsByMaVT = async (maVT: string): Promise<QuyDoiDonViTinhChiTiet[]> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      const response = await api.get<QuyDoiDonViTinhChiTietResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.QUY_DOI_DON_VI_TINH_CHI_TIET}/?ma_vt=${maVT}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching detailed unit conversions by material:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchQuyDoiDonViTinhChiTiets(0, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity?.slug]);

  return {
    quyDoiDonViTinhChiTiets,
    isLoading,
    totalItems,
    currentPage,
    handlePageChange,
    addQuyDoiDonViTinhChiTiet,
    updateQuyDoiDonViTinhChiTiet,
    deleteQuyDoiDonViTinhChiTiet,
    refreshQuyDoiDonViTinhChiTiets,
    getQuyDoiDonViTinhChiTietsByMaVT
  };
};

export const fetchAllQuyDoiDonViTinhChiTiet = async (
  entitySlug: string,
  params: Record<string, any> = {},
  pageSize = 200
): Promise<QuyDoiDonViTinhChiTiet[]> => {
  let page = 1;
  let all: QuyDoiDonViTinhChiTiet[] = [];
  let totalCount = 0;

  while (true) {
    const response = await api.get<QuyDoiDonViTinhChiTietResponse>(
      `/entities/${entitySlug}/erp/${QUERY_KEYS.QUY_DOI_DON_VI_TINH_CHI_TIET}/`,
      {
        params: {
          ...params,
          page,
          page_size: pageSize
        }
      }
    );

    const pageResults = response.data.results || [];
    totalCount = response.data.count ?? totalCount;
    all = all.concat(pageResults);

    if (!pageResults.length) {
      break;
    }

    const hasNext = (response.data as any).next;

    if (!hasNext && pageResults.length < pageSize) {
      break;
    }

    if (totalCount && all.length >= totalCount) {
      break;
    }

    page += 1;
  }

  return all;
};
